import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { normalizeHintMarkdown } from '../utils/normalizeHintMarkdown';

interface MarkdownRendererProps {
  content: string | null | undefined;
  className?: string;
}

const readableTextStyle: React.CSSProperties = {
  textAlign: "left",
  wordSpacing: "normal",
  letterSpacing: "normal",
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({ content, className = '' }) => {
  // Parse-once approach: memoize the content to prevent re-parsing
  const memoizedContent = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return '';
    }
    return normalizeHintMarkdown(content);
  }, [content]);

  // Return empty div if no content
  if (!memoizedContent) {
    return null;
  }

  return (
    <div
      className={`markdown-content ${className}`}
      style={{
        fontSize: "inherit",
        color: "inherit",
        fontWeight: "inherit",
        ...readableTextStyle,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Heading styles - inherit parent styles (size, color, weight from parent container)
          // eslint-disable-next-line jsx-a11y/heading-has-content
          h1: ({ node, children, ...props }) => (
            <h1
              className="text-base font-bold mt-3 mb-1 first:mt-0"
              style={{
                // Just slightly larger than the parent text size
                fontSize: "calc(1em + 0.2rem)",
                color: "inherit",
                // fontWeight: "inherit",
                ...readableTextStyle,
              }}
              {...props}
            >
              {children}
            </h1>
          ),
          // eslint-disable-next-line jsx-a11y/heading-has-content
          h2: ({ node, children, ...props }) => (
            <h2
              className="text-base font-bold mt-3 mb-1 first:mt-0"
              style={{
                fontSize: "calc(1em + 0.15rem)",
                color: "inherit",
                // fontWeight: "inherit",
                ...readableTextStyle,
              }}
              {...props}
            >
              {children}
            </h2>
          ),
          // eslint-disable-next-line jsx-a11y/heading-has-content
          h3: ({ node, children, ...props }) => (
            <h3
              className="text-base font-bold mt-3 mb-1 first:mt-0"
              style={{
                fontSize: "calc(1em + 0.15rem)",
                color: "inherit",
                // fontWeight: "inherit",
                ...readableTextStyle,
              }}
              {...props}
            >
              {children}
            </h3>
          ),
          // Paragraph styles - inherit all parent styles (text color, size, weight)
          p: ({ node, children, ...props }) => (
            <p
              className="mb-1"
              style={{
                fontSize: "inherit",
                color: "inherit",
                fontWeight: "inherit",
                ...readableTextStyle,
              }}
              {...props}
            >
              {children}
            </p>
          ),
          // List styles - inherit text color and size but keep list styling
          ul: ({ node, children, ...props }) => (
            <ul
              className="list-disc mb-2 ml-4"
              style={{ color: "inherit", fontSize: "inherit", ...readableTextStyle }}
              {...props}
            >
              {children}
            </ul>
          ),
          ol: ({ node, children, ...props }) => (
            <ol
              className="list-decimal mb-2 ml-4"
              style={{ color: "inherit", fontSize: "inherit", ...readableTextStyle }}
              {...props}
            >
              {children}
            </ol>
          ),
          li: ({ node, children, ...props }) => (
            <li
              className="mb-1"
              style={{ fontSize: "inherit", color: "inherit", ...readableTextStyle }}
              {...props}
            >
              {children}
            </li>
          ),
          // Bold and italic - preserve formatting but inherit color and size
          strong: ({ node, children, ...props }) => (
            <strong
              className="font-bold"
              style={{ color: "inherit", fontSize: "inherit" }}
              {...props}
            >
              {children}
            </strong>
          ),
          em: ({ node, children, ...props }) => (
            <em
              className="italic"
              style={{ color: "inherit", fontSize: "inherit" }}
              {...props}
            >
              {children}
            </em>
          ),
          // Code blocks - keep code styling (don't inherit for code)
          code: ({ node, inline, children, ...props }: any) =>
            inline ? (
              <code
                className="bg-gray-100 px-1 py-0.5 rounded text-sm"
                {...props}
              >
                {children}
              </code>
            ) : (
              <code
                className="block bg-gray-100 p-2 rounded text-sm overflow-x-auto"
                {...props}
              >
                {children}
              </code>
            ),
          // Links - custom colors: text rgb(27, 54, 93), underline rgb(244, 162, 97)
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          a: ({ node, children, ...props }) => (
            <a
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "rgb(27, 54, 93)",
                textDecorationColor: "rgb(244, 162, 97)",
                textDecoration: "underline",
              }}
              {...props}
            >
              {children}
            </a>
          ),
        }}
      >
        {memoizedContent}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
